import { ctaRadius } from '@/lib/brandGradient'
import { memo } from 'react'
import { Link } from 'react-router-dom'
import { HomeScrollReveal } from '@/components/ui/home/HomeScrollReveal'

const TECH_INDUSTRY = 'AI & GenAI'
const FINTECH_INDUSTRY = 'FinTech'
const HEALTH_INDUSTRY = 'HealthTech'

type SpotlightSlide = {
  id: string
  tag: string
  heading: string
  body: string
  ctaLabel: string
  ctaTo: string
}

const SLIDES: SpotlightSlide[] = [
  {
    id: 'ai-gold-rush',
    tag: 'AI & Automation',
    heading: 'AI Gold Rush',
    body: "India's AI Market Could Cross $17 Billion by 2027. India contributes 16% of the world's AI talent pool as businesses race to automate and improve efficiency.",
    ctaLabel: 'Browse AI Ideas',
    ctaTo: `/validate-ideas?industry=${encodeURIComponent(TECH_INDUSTRY)}`,
  },
  {
    id: 'digital-payments',
    tag: 'Fintech',
    heading: '228+ Billion Digital Payments',
    body: 'UPI handled over 228 billion transactions in 2025, making digital payments a default behaviour across India with cross-border payments expanding rapidly.',
    ctaLabel: 'Browse Fintech Ideas',
    ctaTo: `/validate-ideas?industry=${encodeURIComponent(FINTECH_INDUSTRY)}`,
  },
  {
    id: 'healthcare-ai',
    tag: 'healthcare',
    heading: 'Healing with intelligence',
    body: 'From AI-powered clinical assistants to digital health infrastructure, health care providers are increasingly investing in technology that improves care, efficiency and accessibility.',
    ctaLabel: 'Browse HealthTech Ideas',
    ctaTo: `/validate-ideas?industry=${encodeURIComponent(HEALTH_INDUSTRY)}`,
  },
]

function SpotlightTag({ label }: { label: string }) {
  return (
    <span className="absolute top-4 right-4 inline-flex items-center rounded-full border border-[#648381] bg-[#648381]/10 px-2.5 py-1 text-[11px] font-medium tracking-wide text-white">
      {label}
    </span>
  )
}

function SpotlightCard({ slide }: { slide: SpotlightSlide }) {
  return (
    <article className="relative flex h-full min-h-0 w-full flex-col items-center rounded-2xl border border-white/[0.08] bg-[var(--surface-card)] px-6 pt-12 pb-8 text-center shadow-[inset_0_1px_0_0_rgba(255,255,255,0.04)] dark:bg-white/[0.03]">
      <SpotlightTag label={slide.tag} />
      <h3 className="text-[24px] leading-tight font-medium tracking-tight text-[var(--foreground)]">
        {slide.heading}
      </h3>
      <p className="mt-4 mb-7 line-clamp-6 min-h-0 flex-1 text-justify text-[14px] leading-relaxed font-medium text-[var(--foreground-muted)]">
        {slide.body}
      </p>
      <Link
        to={slide.ctaTo}
        className={`mt-auto inline-flex h-[44px] w-[191px] items-center justify-center ${ctaRadius} border-[1.5px] border-[#E0A263]/65 bg-[#F6CB86]/[0.06] text-sm font-semibold text-[var(--foreground)] transition hover:border-[#E0A263]/90 hover:bg-[#F6CB86]/14`}
      >
        {slide.ctaLabel}
      </Link>
    </article>
  )
}

export const CategorySpotlightCarousel = memo(() => {
  return (
    <section
      className="w-full bg-[var(--background)] px-4 py-10 sm:px-6 sm:py-12 lg:py-14"
      aria-labelledby="spotlight-heading"
    >
      <div className="mx-auto w-full max-w-[1200px]">
        <header className="mb-5 sm:mb-6">
          <h2
            id="spotlight-heading"
            className="text-[28px] leading-tight font-medium tracking-tight text-[var(--foreground)]"
          >
            Spotlight
          </h2>
        </header>

        <div className="relative">
          <div className="pointer-events-none absolute inset-0 overflow-hidden rounded-2xl opacity-[0.35]">
            <div className="absolute -top-24 right-0 h-48 w-48 rounded-full bg-[#F6CB86]/20 blur-3xl" />
            <div className="absolute -bottom-16 left-1/4 h-40 w-40 rounded-full bg-[#E0A263]/15 blur-3xl" />
          </div>

          <div className="relative grid grid-cols-1 gap-4 sm:grid-cols-3 sm:items-stretch">
            {SLIDES.map((slide, i) => (
              <HomeScrollReveal
                key={slide.id}
                index={i}
                className="flex h-full min-h-0 w-full"
              >
                <SpotlightCard slide={slide} />
              </HomeScrollReveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  )
})

CategorySpotlightCarousel.displayName = 'CategorySpotlightCarousel'
